import { useProfileQuery } from "@/hooks/api/profile.queries";
import { useActiveInternshipQuery } from "@/hooks/api/internship.queries";
import { Card, CardContent, CardHeader, CardTitle, PageContainer, Spinner } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Edit,
  Linkedin,
  Github,
  Globe,
  Mail,
  Phone,
  GraduationCap,
  MapPin,
  Briefcase,
  Calendar,
  Building,
  User,
  Sparkles,
  CheckCircle2,
  XCircle,
  Building2,
  CalendarDays,
  Award
} from "lucide-react";
import { useState } from "react";
import ProfileForm from "./ProfileForm";
import { getProfilePhotoUrl } from "@/services/profile.service";
import { cn } from "@/lib/utils";
import { formatDate } from "@/utils/formatDate.util";

const Profile = () => {
  const { data: profile, isLoading: isProfileLoading, isError: isProfileError, refetch: refetchProfile } = useProfileQuery();
  const { data: activeInternship } = useActiveInternshipQuery();
  const [isEditing, setIsEditing] = useState(false);

  const isLoading = isProfileLoading;
  const isError = isProfileError;
  const refetch = refetchProfile;

  if (isLoading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner />
          <p className="text-muted-foreground animate-pulse text-sm font-medium">Curating your profile...</p>
        </div>
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <PageContainer className="flex h-[70vh] items-center justify-center">
        <Card className="w-full max-w-md text-center p-8 border-dashed">
          <CardContent className="space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center text-red-500">
              <XCircle size={24} />
            </div>
            <h3 className="text-xl font-bold">Failed to load profile</h3>
            <p className="text-muted-foreground text-sm">We're having trouble reaching the campus servers.</p>
            <Button onClick={() => refetch()} variant="outline" className="w-full">Retry Connection</Button>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  if (isEditing) {
    return <ProfileForm initialData={profile} onCancel={() => setIsEditing(false)} />;
  }

  const socialLinks = [
    { icon: Linkedin, url: profile.linkedin_url, label: "LinkedIn", color: "text-blue-600 hover:bg-blue-50" },
    { icon: Github, url: profile.github_url, label: "GitHub", color: "text-gray-900 hover:bg-gray-100" },
    { icon: Globe, url: profile.portfolio_url, label: "Portfolio", color: "text-indigo-600 hover:bg-indigo-50" },
  ].filter(link => link.url);

  return (
    <PageContainer className="space-y-8 pb-12">
      {/* ─── Header / Hero Section ─── */}
      <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 p-8 text-white shadow-2xl">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 h-96 w-96 rounded-full bg-white/10 blur-3xl opacity-50" />
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 h-64 w-64 rounded-full bg-indigo-400/20 blur-3xl" />

        <div className="relative flex flex-col md:flex-row gap-8 items-center md:items-start text-center md:text-left">
          {/* Avatar Area */}
          <div className="relative group">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-3xl overflow-hidden border-4 border-white/20 bg-white/10 backdrop-blur-md shadow-2xl transition-transform duration-500 group-hover:scale-[1.02]">
              {profile.profile_photo_url ? (
                <img
                  src={getProfilePhotoUrl(profile.profile_photo_url, 'lg') || ""}
                  alt={profile.full_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <User size={64} className="text-white/40" />
                </div>
              )}
            </div>
            {profile.is_eligible && (
              <div className="absolute -bottom-2 -right-2 bg-emerald-500 text-white p-1.5 rounded-full border-4 border-indigo-600 shadow-lg">
                <CheckCircle2 size={20} />
              </div>
            )}
          </div>

          <div className="flex-1 space-y-4">
            <div className="space-y-1">
              <div className="flex items-center justify-center md:justify-start gap-3">
                <h1 className="text-3xl md:text-5xl font-black tracking-tight">{profile.full_name}</h1>
                <Badge variant="outline" className="bg-white/10 border-white/20 text-white text-[10px] uppercase font-black px-2 mt-1">
                  {profile.roll_number || "Student"}
                </Badge>
              </div>
              <p className="text-indigo-100/90 font-medium flex items-center justify-center md:justify-start gap-2">
                <GraduationCap size={18} />
                {profile.department} • Batch of {profile.graduation_year}
              </p>
            </div>

            <div className="flex flex-wrap justify-center md:justify-start gap-4">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/10 text-sm font-medium">
                <Mail size={16} className="text-indigo-200" />
                {profile.org_email}
              </div>
              {profile.personal_email && (
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/10 text-sm font-medium" title="Personal Email">
                  <Mail size={16} className="text-indigo-200 opacity-70" />
                  {profile.personal_email}
                </div>
              )}
              {profile.phone && (
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-2xl border border-white/10 text-sm font-medium">
                  <Phone size={16} className="text-indigo-200" />
                  {profile.phone}
                </div>
              )}
            </div>

            <div className="pt-2 flex justify-center md:justify-start gap-3">
              <Button
                onClick={() => setIsEditing(true)}
                className="bg-white text-indigo-600 hover:bg-white/90 font-bold px-8 rounded-2xl shadow-xl shadow-indigo-900/30"
              >
                <Edit className="mr-2 h-4 w-4" /> Edit Profile
              </Button>
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white/10 border border-white/20 hover:bg-white hover:text-indigo-600 transition-all duration-300"
                  title={link.label}
                >
                  <link.icon size={20} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Bio & Preferences */}
        <div className="lg:col-span-2 space-y-8">
          {/* About */}
          <Section cardTitle="About Me" icon={Sparkles}>
            <p className="text-muted-foreground leading-relaxed text-lg italic bg-indigo-50/30 p-6 rounded-3xl border-l-4 border-indigo-400 whitespace-pre-wrap break-words">
              {profile.bio || "No bio added yet. Share a bit about your journey!"}
            </p>
          </Section>

          {/* Interests & Preferences */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="rounded-[2rem] border-none bg-slate-50/50 shadow-sm overflow-hidden group">
              <CardHeader className="bg-white border-b border-slate-100 py-4 px-6">
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-indigo-600 uppercase tracking-widest">
                  <Briefcase size={16} /> Preferred Domains
                </CardTitle>

              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-2">
                  {profile.preferred_domains && profile.preferred_domains.length > 0 ? profile.preferred_domains.map(domain => (
                    <Badge key={domain} className="bg-white text-slate-700 hover:bg-indigo-600 hover:text-white border-slate-200 px-4 py-1.5 rounded-xl transition-all shadow-sm">
                      {domain}
                    </Badge>
                  )) : (
                    <p className="text-slate-400 text-sm italic">No preferred domains set.</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-[2rem] border-none bg-slate-50/50 shadow-sm overflow-hidden group">
              <CardHeader className="bg-white border-b border-slate-100 py-4 px-6">
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-indigo-600 uppercase tracking-widest">
                  <MapPin size={16} /> Locations
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-2">
                  {profile.preferred_locations && profile.preferred_locations.length > 0 ? profile.preferred_locations.map(loc => (
                    <Badge key={loc} className="bg-white text-slate-700 hover:bg-violet-600 hover:text-white border-slate-200 px-4 py-1.5 rounded-xl transition-all shadow-sm">
                      {loc}
                    </Badge>
                  )) : (
                    <p className="text-slate-400 text-sm italic">No preferred locations set.</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Right Column: Academic Details & Eligibility */}
        <div className="space-y-8">
          <Section cardTitle="Academic Stats" icon={GraduationCap}>
            <div className="space-y-6">
              <DetailRow label="Department" value={profile.department || "-"} icon={Building} />
              <DetailRow label="Year of Study" value={`${profile.year_of_study}${getOrdinalNum(profile.year_of_study || 0)} Year`} icon={Calendar} />
              <DetailRow label="Graduating in" value={profile.graduation_year?.toString() || "-"} icon={Calendar} />

              <div className="pt-4 border-t border-slate-100 space-y-4">
                <div className="flex items-center justify-between p-4 rounded-2xl bg-indigo-50/50 border border-indigo-100">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className={cn("h-5 w-5", profile.is_eligible ? "text-emerald-500" : "text-slate-300")} />
                    <span className="text-sm font-bold text-slate-700">Internship Eligibility</span>
                  </div>
                  <Badge variant="outline" className={cn(profile.is_eligible ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 text-slate-500")}>
                    {profile.is_eligible ? "Eligible" : "Pending"}
                  </Badge>
                </div>

                <div className="flex items-center justify-between p-4 rounded-2xl bg-violet-50/50 border border-violet-100">
                  <div className="flex items-center gap-3">
                    <Briefcase className={cn("h-5 w-5", profile.is_open_to_work ? "text-violet-500" : "text-slate-300")} />
                    <span className="text-sm font-bold text-slate-700">Open to Work</span>
                  </div>
                  <Badge variant="outline" className={cn(profile.is_open_to_work ? "bg-violet-500 text-white border-violet-600" : "bg-slate-50 text-slate-500")}>
                    {profile.is_open_to_work ? "Actively Seeking" : "Passive"}
                  </Badge>
                </div>
              </div>
            </div>
          </Section>

          {activeInternship?.has_active_internship && activeInternship?.offer && (
            <Card className="rounded-[2rem] border-none bg-gradient-to-br from-emerald-600 to-teal-700 p-6 text-white shadow-xl overflow-hidden relative group">
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-all" />
              <div className="relative space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Award size={20} className="text-emerald-100" />
                  </div>
                  <Badge variant="outline" className="bg-white/20 border-white/30 text-white text-[10px] uppercase font-black">Active Placement</Badge>
                </div>
                <div>
                  <h4 className="text-xl font-black">{activeInternship.offer.internship_title || "Internship role"}</h4>
                  <div className="flex items-center gap-2 text-emerald-100/80 text-sm font-medium mt-1">
                    <Building2 size={14} />
                    {activeInternship.offer.company_name || "Company not shared"}
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <div className="text-[10px] uppercase tracking-tighter font-bold text-emerald-200/60">Accepted Offer</div>
                  <div className="flex items-center gap-1 text-xs font-bold bg-white/10 px-3 py-1 rounded-lg">
                    <CalendarDays size={12} />{" "}
                    {activeInternship.offer.start_date
                      ? formatDate(activeInternship.offer.start_date, { timeZone: "UTC" })
                      : "Start date TBC"}
                  </div>
                </div>
              </div>
            </Card>
          )}

          <Card className="rounded-[2rem] border-none bg-gradient-to-tr from-slate-900 to-indigo-950 p-6 text-white shadow-xl">
            <div className="flex flex-col gap-4">
              <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                <Sparkles size={20} className="text-indigo-400" />
              </div>
              <h4 className="text-lg font-bold">Complete your skills</h4>
              <p className="text-slate-400 text-xs">A more detailed skill profile increases your chances by 40%.</p>
              <Button variant="outline" className="bg-gray-700 h-10 border-white/10 hover:bg-white/10! hover:text-white mt-2 rounded-xl" asChild>
                <a href="/profile/skills">Go to Skills</a>
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </PageContainer>
  );
};

function Section({ children, cardTitle, icon: Icon }: { children: React.ReactNode, cardTitle: string, icon: any }) {
  return (
    <Card className="rounded-[2.5rem] border-none shadow-xl shadow-slate-200/50 overflow-hidden bg-white">
      <CardHeader className="border-b border-slate-50 py-6 px-8">
        <CardTitle className="text-xl font-black flex items-center gap-3 text-slate-900">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
            <Icon size={18} />
          </div>
          {cardTitle}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-8">
        {children}
      </CardContent>
    </Card>
  );
}

function DetailRow({ label, value, icon: Icon }: { label: string, value: string, icon: any }) {
  return (
    <div className="flex items-center gap-4 group">
      <div className="w-10 h-10 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center transition-colors group-hover:bg-indigo-50 group-hover:text-indigo-600">
        <Icon size={18} />
      </div>
      <div>
        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{label}</p>
        <p className="text-base font-bold text-slate-900 tracking-tight">{value}</p>
      </div>
    </div>
  );
}

function getOrdinalNum(n: number) {
  return n > 0 ? (['th', 'st', 'nd', 'rd'][(n % 10 > 3 || Math.floor(n % 100 / 10) === 1) ? 0 : n % 10]) : "";
}

export default Profile;

